import {
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDoc,
  doc,
} from "firebase/firestore";
import { NotificationsAdminRef, db } from "../../../firebase";

export const getSingleUser = async (userId) => {
  try {
    return await getDoc(doc(db, "users", userId));
  } catch (error) {
    console.log("error getting user:", error.message);
  }
};

export const getSingleQuickTale = async (qtId, collectionName = "quicktales") => {
  try {
    const safeCollection =
      collectionName === "batchQuicktales" ? "batchQuicktales" : "quicktales";
    return await getDoc(doc(db, safeCollection, qtId));
  } catch (error) {
    console.log("error getting quicktale:", error.message);
  }
};

export const getAllNotifications = async (organizationId, pageSize = 25, lastDoc = null) => {
  try {
    console.log("getAllNotifications called with:", { organizationId, pageSize, lastDoc });
    
    let queryRef = query(
      NotificationsAdminRef,
      where("organization", "==", organizationId),
      orderBy("createdAt", "desc"),
      limit(pageSize)
    );

    if (lastDoc) {
      queryRef = query(
        NotificationsAdminRef,
        where("organization", "==", organizationId),
        orderBy("createdAt", "desc"),
        startAfter(lastDoc),
        limit(pageSize)
      );
    }

    console.log("Executing Firestore query...");
    const response = await getDocs(queryRef);
    console.log("Raw Firestore response docs count:", response.docs.length);
    
    if (response.docs.length > 0) {
      console.log("First notification raw data:", response.docs[0].data());
    }
    
    // Fetch additional data for each notification
    const notificationsWithData = await Promise.all(
      response.docs.map(async (notificationDoc) => {
        const notificationData = notificationDoc.data();
        console.log("Processing notification:", notificationData);
        
        // Fetch user data (narrator updates may have no user)
        const userDoc = notificationData.user
          ? await getSingleUser(notificationData.user)
          : null;
        const userData = userDoc?.data() || null;
        console.log("User data:", userData);
        
        // Fetch quicktale data (narrator updates may target batchQuicktales)
        const quicktaleDoc = await getSingleQuickTale(
          notificationData.object,
          notificationData.collection
        );
        const quicktaleData = quicktaleDoc?.data() || null;
        console.log("QuickTale data:", quicktaleData);
        console.log("QuickTale photoUrl:", quicktaleData?.photoUrl);
        console.log("QuickTale imageUrls:", quicktaleData?.imageUrls);
        
        return {
          id: notificationDoc.id,
          ...notificationData,
          userData,
          quicktaleData,
        };
      })
    );

    console.log("Final notifications with data:", notificationsWithData);

    return {
      notifications: notificationsWithData,
      lastDoc: response.docs[response.docs.length - 1] || null,
      hasMore: response.docs.length === pageSize,
    };
  } catch (error) {
    console.log("Error fetching notifications from Firestore:", error.message);
    console.error("Full error:", error);
    return {
      notifications: [],
      lastDoc: null,
      hasMore: false,
    };
  }
};

export const notificationsSearch = (array, keyword) => {
  const searchTerm = keyword.toLowerCase();
  return array.filter((notification) => {
    return (
      (notification.userData?.firstName &&
        notification.userData.firstName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (notification.userData?.lastName &&
        notification.userData.lastName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (notification.userData?.userName &&
        notification.userData.userName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (notification.narratorName &&
        notification.narratorName
          .toLowerCase()
          .match(new RegExp(searchTerm, "g"))) ||
      (notification.quicktaleData?.title &&
        notification.quicktaleData.title
          .toLowerCase()
          .match(new RegExp(searchTerm, "g")))
    );
  });
};

export const assembleNotificationMessage = (notification) => {
  const { userData, quicktaleData, type, status } = notification;
    let userName = 'A Guest';
    let userHandle = '';
  if (userData) {
    userName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'A user';
    userHandle = userData?.userName ? `(@${userData.userName})` : '';
  }

    const quicktaleTitle = quicktaleData?.title || 'a QuickTale';

  // Handle QuickTale reaction notifications
    switch (type) {
      case 'quicktale_submission':
        if (status === 'pending') {
          return `QuickTale Submitted, Pending Approval.  ${userName} ${userHandle} submitted the QuickTale, "${quicktaleTitle}."`;
        } else if (status === 'approved') {
          return `QuickTale Submitted, Auto Approved.  ${userName} ${userHandle} submitted the QuickTale, "${quicktaleTitle}."`;
        }
        return `QuickTale Submitted. ${userName} ${userHandle} submitted the QuickTale, "${quicktaleTitle}".`;
      
      case 'quicktale_reaction':
        if (status === 'pending') {
          return `Audio Reaction Submitted, Pending Approval.  ${userName} ${userHandle} submitted an Audio Reaction for, "${quicktaleTitle}."`;
        } else if (status === 'approved') {
          return `Audio Reaction Submitted, Auto Approved.  ${userName} ${userHandle} submitted an Audio Reaction for, "${quicktaleTitle}."`;
        }
        return `Audio Reaction Submitted. ${userName} ${userHandle} submitted an Audio Reaction for, "${quicktaleTitle}."`;
      
      case 'contributor_request':
        if (status === 'pending') {
          return `Contributor Request, Pending Approval.  ${userName} ${userHandle} requested to become a Contributor.`;
        } else if (status === 'approved') {
          return `Contributor Request, Auto Approved.  ${userName} ${userHandle} requested to become a Contributor.`;
        }
        return `Contributor Request.  ${userName} ${userHandle} requested to become a Contributor.`;

      case 'quicktale_update': {
        const narratorName =
          (userData
            ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
            : '') ||
          notification.narratorName ||
          'A Guest';
        const changes = Array.isArray(notification.changes) ? notification.changes : [];
        const changesText = changes.length ? ` (${changes.join(', ')})` : '';
        return `QuickTale Updated. ${narratorName} updated the QuickTale, "${quicktaleTitle}"${changesText}.`;
      }

      default:
        return `${userName} ${userHandle} performed an action on "${quicktaleTitle}".`;
    }
};
